import { useCallback, useMemo } from 'react'

import { useMediaPlayerStateEvent } from '@verbit-ai/verbit-connect-web/player-provider'

import { useTranscriptClient, useTranscriptParagraphIds, useTranscriptTimings } from 'src/contexts/Transcript/TranscriptContext'
import { TranscriptParagraph, WordTiming } from 'src/models/Transcript'
import { findIndexBinary } from 'src/utils/array'
import { TimingUtils, WORD_GRACE_SECONDS } from 'src/utils/timings'

export const useActiveMediaTimingLookup = () => {
    const transcriptClient = useTranscriptClient()

    const paragraphIds = useTranscriptParagraphIds()
    const timings = useTranscriptTimings()
    const paragraphIndexById = useMemo(() => new Map(paragraphIds.map((pId, index) => [pId, index])), [paragraphIds])

    return useCallback(
        (currentTime: number): WordTiming | null => {
            // The ids are from the last render but the client is live: a paragraph merged or deleted since reads as untimed.
            const paragraphTimes = (pId: string) => {
                const paragraph = transcriptClient.getParagraphById(pId) as TranscriptParagraph | undefined

                return { startTime: paragraph?.startTime ?? null, endTime: paragraph?.endTime ?? null }
            }

            /**
             * The end time of the nearest timed paragraph at or before `index`. An untimed paragraph (empty, or not timed
             * yet) borrows it, so the binary search always knows which way to go; `cache` walks a long untimed run once.
             */
            const endTimeAtOrBefore = (index: number, cache: Map<number, number | null>) => {
                const walked: number[] = []
                let endTime: number | null = null
                for (let i = index; i >= 0; i--) {
                    const cached = cache.get(i)
                    if (cached !== undefined) {
                        endTime = cached
                        break
                    }
                    walked.push(i)
                    endTime = paragraphTimes(paragraphIds[i]).endTime
                    if (endTime !== null) {
                        break
                    }
                }
                walked.forEach((i) => cache.set(i, endTime))

                return endTime
            }

            const findParagraphIndex = (time: number) => {
                const borrowedEndTimes = new Map<number, number | null>()

                return findIndexBinary({
                    array: paragraphIds,
                    // Half-open [start, end), so where one paragraph ends as the next starts, the next one wins.
                    isGuessMatchingTarget: (pId) => {
                        const { startTime, endTime } = paragraphTimes(pId)

                        return startTime !== null && endTime !== null && time >= startTime && time < endTime
                    },
                    isGuessBelowTarget: (pId) => {
                        const endTime = endTimeAtOrBefore(paragraphIndexById.get(pId)!, borrowedEndTimes)

                        return endTime === null || time >= endTime
                    },
                })
            }

            // Between paragraphs, the one that ended within the grace window still owns its last word.
            let paragraphIndex = findParagraphIndex(currentTime)
            if (paragraphIndex === -1) {
                paragraphIndex = findParagraphIndex(currentTime - WORD_GRACE_SECONDS)
            }

            if (paragraphIndex === -1) {
                return null
            }

            return TimingUtils.getTimingByMediaOffset(timings[paragraphIds[paragraphIndex]] ?? [], currentTime)
        },
        [transcriptClient, paragraphIds, paragraphIndexById, timings],
    )
}

export const useActiveMediaTimingEvent = (callback: (timing: WordTiming | null) => void) => {
    const findWordAt = useActiveMediaTimingLookup()

    useMediaPlayerStateEvent(({ currentTime, isMediaPlaying }) => callback(isMediaPlaying ? findWordAt(currentTime) : null))
}
